import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { ProductsProvider } from './context/ProductsContext';
import Layout from './components/layout/Layout';

// ✅ LAZY LOADED: These pages are now only downloaded when the user clicks on them!
const Home = lazy(() => import('./pages/Home'));
const Shop = lazy(() => import('./pages/Shop'));
const ProductDetails = lazy(() => import('./pages/ProductDetails'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Policies = lazy(() => import('./pages/Policies'));
const InstagramFeed = lazy(() => import('./pages/InstagramFeed'));

// Admin Pages (These are the most important to lazy load!)
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const Products = lazy(() => import('./pages/admin/Products'));
const AdminLogin = lazy(() => import('./pages/admin/Login'));
const Orders = lazy(() => import('./pages/admin/Orders'));
const PromoCodes = lazy(() => import('./pages/admin/PromoCodes'));

// Simple fallback spinner for loading pages
const PageFallback = () => (
  <div className="min-h-screen flex justify-center items-center bg-white">
    <div className="w-8 h-8 border border-pink-500/30 border-t-pink-500 rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ProductsProvider>
          <CartProvider>
            <Router>
              <div className="App">
                <Layout>
                  {/* Wrap Routes in Suspense to show spinner while lazy-loaded code downloads */}
                  <Suspense fallback={<PageFallback />}>
                    <Routes>
                      {/* Public Routes - KEEP EXACTLY AS THEY ARE */}
                      <Route path="/" element={<Home />} />
                      <Route path="/shop" element={<Shop />} />
                      <Route path="/product/:id" element={<ProductDetails />} />
                      <Route path="/checkout" element={<Checkout />} />
                      <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/policies" element={<Policies />} />
                      <Route path="/instagram" element={<InstagramFeed />} />

                      {/* Admin Routes - KEEP EXACTLY AS THEY ARE */}
                      <Route path="/admin" element={<Dashboard />} />
                      <Route path="/admin/login" element={<AdminLogin />} />
                      <Route path="/admin/products" element={<Products />} />
                      <Route path="/admin/products/new" element={<Products />} />
                      <Route path="/admin/products/:id" element={<Products />} />
                      <Route path="/admin/orders" element={<Orders />} />
                      <Route path="/admin/orders/:id" element={<Orders />} />
                      <Route path="/admin/promo-codes" element={<PromoCodes />} />
                      <Route path="/admin/promo-codes/new" element={<PromoCodes />} />
                      <Route path="/admin/promo-codes/:id" element={<PromoCodes />} />
                    </Routes>
                  </Suspense>
                </Layout>
                
                <ToastContainer
                  position="top-right"
                  autoClose={3000}
                  hideProgressBar={false}
                  newestOnTop
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                />
              </div>
            </Router>
          </CartProvider>
        </ProductsProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;